import LoadingItem from "@/components/LoadingItem";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Country, CountrySelect } from "@/components/country-select";
import CitySelect from "@/components/checkout/CitySelect";
import { DatePicker } from "@/components/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LanguageSelector } from "@/components/LanguageSelector";
import { ModeToggle } from "@/components/ThemeToggle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  PrivacyPolicyDialog,
  useStructurePrivacy,
} from "@/components/PrivacyPolicyDialog";
import { environment } from "@/config/env";
import { useFormatDate } from "@/hooks/useFormatDate";
import {
  initTracking,
  trackDatesSelected,
  trackFormSubmitted,
  trackPageView,
  TrackingConfig,
} from "@/utils/tracking";
import axios from "axios";
import { differenceInCalendarDays } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertCircle,
  Bed,
  CalendarCheck,
  CalendarDays,
  CheckCircle2,
  Clock,
  HeartHandshake,
  HelpCircle,
  Info,
  Loader2,
  Mail,
  MapPin,
  PawPrint,
  Phone,
  Plus,
  Send,
  ShieldCheck,
  Sparkles,
  Star,
  StickyNote,
  Trash2,
  User,
  Users,
  Utensils,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

interface Accommodation {
  numAdulti: number;
  numBambini: number;
  childrenAges: number[];
  preferenzaCamera: string;
  trattamento: string;
}

const PHONE_PREFIXES = [
  { code: "+39", flag: "it", name: "Italia (+39)" },
  { code: "+49", flag: "de", name: "Deutschland (+49)" },
  { code: "+43", flag: "at", name: "Österreich (+43)" },
  { code: "+41", flag: "ch", name: "Schweiz / Svizzera (+41)" },
  { code: "+33", flag: "fr", name: "France (+33)" },
  { code: "+44", flag: "gb", name: "United Kingdom (+44)" },
  { code: "+34", flag: "es", name: "España (+34)" },
  { code: "+31", flag: "nl", name: "Nederland (+31)" },
  { code: "+32", flag: "be", name: "Belgique (+32)" },
  { code: "+1", flag: "us", name: "USA / Canada (+1)" },
  { code: "+355", flag: "al", name: "Shqipëria (+355)" },
  { code: "+376", flag: "ad", name: "Andorra (+376)" },
  { code: "+54", flag: "ar", name: "Argentina (+54)" },
  { code: "+61", flag: "au", name: "Australia (+61)" },
  { code: "+971", flag: "ae", name: "UAE (+971)" },
  { code: "+375", flag: "by", name: "Belarus (+375)" },
  { code: "+387", flag: "ba", name: "Bosnia (+387)" },
  { code: "+55", flag: "br", name: "Brasil (+55)" },
  { code: "+359", flag: "bg", name: "Bulgaria (+359)" },
  { code: "+56", flag: "cl", name: "Chile (+56)" },
  { code: "+86", flag: "cn", name: "China (+86)" },
  { code: "+57", flag: "co", name: "Colombia (+57)" },
  { code: "+385", flag: "hr", name: "Croatia (+385)" },
  { code: "+357", flag: "cy", name: "Cyprus (+357)" },
  { code: "+420", flag: "cz", name: "Czech Republic (+420)" },
  { code: "+45", flag: "dk", name: "Danmark (+45)" },
  { code: "+20", flag: "eg", name: "Egypt (+20)" },
  { code: "+372", flag: "ee", name: "Estonia (+372)" },
  { code: "+358", flag: "fi", name: "Finland (+358)" },
  { code: "+30", flag: "gr", name: "Greece (+30)" },
  { code: "+354", flag: "is", name: "Iceland (+354)" },
  { code: "+91", flag: "in", name: "India (+91)" },
  { code: "+353", flag: "ie", name: "Ireland (+353)" },
  { code: "+972", flag: "il", name: "Israel (+972)" },
  { code: "+81", flag: "jp", name: "Japan (+81)" },
  { code: "+383", flag: "xk", name: "Kosovo (+383)" },
  { code: "+371", flag: "lv", name: "Latvia (+371)" },
  { code: "+370", flag: "lt", name: "Lithuania (+370)" },
  { code: "+352", flag: "lu", name: "Luxembourg (+352)" },
  { code: "+356", flag: "mt", name: "Malta (+356)" },
  { code: "+52", flag: "mx", name: "Mexico (+52)" },
  { code: "+377", flag: "mc", name: "Monaco (+377)" },
  { code: "+382", flag: "me", name: "Montenegro (+382)" },
  { code: "+47", flag: "no", name: "Norge (+47)" },
  { code: "+48", flag: "pl", name: "Polska (+48)" },
  { code: "+351", flag: "pt", name: "Portugal (+351)" },
  { code: "+40", flag: "ro", name: "Romania (+40)" },
  { code: "+378", flag: "sm", name: "San Marino (+378)" },
  { code: "+381", flag: "rs", name: "Srbija (+381)" },
  { code: "+421", flag: "sk", name: "Slovensko (+421)" },
  { code: "+386", flag: "si", name: "Slovenija (+386)" },
  { code: "+46", flag: "se", name: "Sverige (+46)" },
  { code: "+90", flag: "tr", name: "Türkiye (+90)" },
  { code: "+380", flag: "ua", name: "Ukraine (+380)" },
];

export default function PreventiveRequest() {
  const { t } = useTranslation("preventiveRequest");
  const { executeRecaptcha } = useGoogleReCaptcha();
  const routeParams = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { formatDate } = useFormatDate();

  // Section refs for smooth scrolling on validation error
  const datesSectionRef = useRef<HTMLDivElement>(null);
  const accommodationsSectionRef = useRef<HTMLDivElement>(null);
  const contactsSectionRef = useRef<HTMLDivElement>(null);
  const privacySectionRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [structure, setStructure] = useState<any>();
  const [isPrivacyDialogOpen, setIsPrivacyDialogOpen] = useState(false);
  const [policyModalType, setPolicyModalType] = useState<
    "privacy" | "marketing"
  >("privacy");
  const { privacyPolicyHtml, marketingPolicyHtml } = useStructurePrivacy(
    structure?.id ?? structure?.Id,
  );
  const [rooms, setRooms] = useState<
    { id?: number; code: string; description: string }[]
  >([]);
  const [boards, setBoards] = useState<{ code: string; description: string }[]>(
    [],
  );
  const [trackingConfig, setTrackingConfig] = useState<TrackingConfig>({});
  const datesTrackedRef = useRef(false);

  const [formConfig, setFormConfig] = useState({
    showRooms: true,
    showBoard: true,
    showNote: true,
    showNumberChildren: true,
    showPets: true,
    maxAgeChildren: 17,
    minStay: 0,
    availableDays: {
      sunday: true,
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
    },
  });

  const [checkin, setCheckin] = useState<Date | undefined>();
  const [checkout, setCheckout] = useState<Date | undefined>();
  const [accommodations, setAccommodations] = useState<Accommodation[]>([
    {
      numAdulti: 2,
      numBambini: 0,
      childrenAges: [],
      preferenzaCamera: "nessuna",
      trattamento: "nessuna",
    },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [animaliDomestici, setAnimaliDomestici] = useState(false);

  const [titolo, setTitolo] = useState("sig");
  const [nome, setNome] = useState("");
  const [cognome, setCognome] = useState("");
  const [lingua, setLingua] = useState("it");
  const [nazione, setNazione] = useState<Country | undefined>(undefined);
  const [citta, setCitta] = useState("");
  const [email, setEmail] = useState("");
  const [telefono, setTelefono] = useState("");
  const [prefissoTelefono, setPrefissoTelefono] = useState("+39");
  const [note, setNote] = useState("");
  const [accettaPrivacy, setAccettaPrivacy] = useState(false);
  const [accettaMarketing, setAccettaMarketing] = useState(false);

  // Form touched & validation states
  const [emailTouched, setEmailTouched] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  useEffect(() => {
    if (!routeParams.id) return;
    axios.defaults.baseURL = environment.ELITE_BASE_URI;
    axios
      .get(`/v1/preventiveRequest/${routeParams.id}/web-form-config`)
      .then((res) => {
        setFormConfig({
          showRooms: res.data?.showRooms ?? true,
          showBoard: res.data?.showBoard ?? true,
          showNote: res.data?.showNote ?? true,
          showNumberChildren: res.data?.showNumberChildren ?? true,
          showPets: res.data?.showPets ?? true,
          maxAgeChildren: res.data?.maxAgeChildren ?? 17,
          minStay: res.data?.minStay ?? 0,
          availableDays: res.data?.availableDays ?? {
            sunday: true,
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: true,
          },
        });
        const trackingData: TrackingConfig = {
          gtmEnabled: res.data?.gtmEnabled,
          gtmId: res.data?.gtmId,
          ga4Enabled: res.data?.ga4Enabled,
          ga4MeasurementId: res.data?.ga4MeasurementId,
          metaPixelEnabled: res.data?.metaPixelEnabled,
          metaPixelId: res.data?.metaPixelId,
        };
        setTrackingConfig(trackingData);
        initTracking(trackingData);
        trackPageView(trackingData);

        return axios.get(`/v1/preventiveRequest/${routeParams.id}`);
      })
      .then((response) => {
        setStructure(response.data?.structure);
        return axios.get(
          `/v1/preventiveRequest/${routeParams.id}/boards-and-rooms`,
        );
      })
      .then((res) => {
        setRooms(res.data?.rooms || []);
        setBoards(res.data?.boards || []);
        setLoading(false);
      })
      .catch((err) => {
        setIsError(true);
        setErrorMessage(
          err.response?.data?.message || err.message || t("connectionError"),
        );
        setLoading(false);
      });
  }, [routeParams.id, t]);

  useEffect(() => {
    if (checkin && checkout && !datesTrackedRef.current) {
      datesTrackedRef.current = true;
      trackDatesSelected(trackingConfig, checkin, checkout);
    }
  }, [checkin, checkout, trackingConfig]);

  const isDayDisabled = (date: Date) => {
    const dayNames = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    return !formConfig.availableDays[dayNames[date.getDay()]];
  };

  const checkoutMinDate = useMemo(() => {
    if (!checkin) return new Date();
    const daysToAdd = Math.max(1, formConfig.minStay);
    return new Date(checkin.getTime() + daysToAdd * 86400000);
  }, [checkin, formConfig.minStay]);

  // Stay nights calculation
  const nightsCount = useMemo(() => {
    if (!checkin || !checkout) return 0;
    return Math.max(0, differenceInCalendarDays(checkout, checkin));
  }, [checkin, checkout]);

  // Total guests calculation
  const totalAdults = useMemo(
    () => accommodations.reduce((acc, curr) => acc + (curr.numAdulti || 0), 0),
    [accommodations],
  );
  const totalChildren = useMemo(
    () => accommodations.reduce((acc, curr) => acc + (curr.numBambini || 0), 0),
    [accommodations],
  );
  const totalGuests = totalAdults + totalChildren;

  const updateAccommodation = (
    index: number,
    field: keyof Accommodation,
    value: any,
  ) => {
    setAccommodations((prev) =>
      prev.map((acc, i) => (i === index ? { ...acc, [field]: value } : acc)),
    );
  };

  const validateEmail = (value: string) => {
    if (!value.trim()) return t("emailRequired");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
      return t("emailInvalid");
    }
    return "";
  };

  const emailError =
    emailTouched || hasAttemptedSubmit ? validateEmail(email) : "";
  const emailValid = !validateEmail(email);

  const allChildrenAgesFilled = accommodations.every((acc) => {
    if (acc.numBambini === 0) return true;
    return (
      acc.childrenAges.length === acc.numBambini &&
      acc.childrenAges.every(
        (age) => age !== undefined && age !== null && age >= 0,
      )
    );
  });

  const canSubmit =
    Boolean(checkin) &&
    Boolean(checkout) &&
    Boolean(nome.trim()) &&
    Boolean(cognome.trim()) &&
    emailValid &&
    Boolean(telefono.trim()) &&
    Boolean(lingua?.trim()) &&
    accettaPrivacy &&
    allChildrenAgesFilled;

  const handleSubmit = async () => {
    setHasAttemptedSubmit(true);

    if (!canSubmit) {
      // Find what is missing and scroll to it
      if (!checkin || !checkout) {
        toast.error(t("datesRequired"));
        datesSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        return;
      }
      if (!allChildrenAgesFilled) {
        toast.error(t("childrenAgesRequired"));
        accommodationsSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        return;
      }
      if (!nome.trim() || !cognome.trim() || !emailValid || !telefono.trim() || !lingua?.trim()) {
        toast.error(t("missingFieldsWarning"));
        contactsSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        return;
      }
      if (!accettaPrivacy) {
        toast.error(t("privacyRequired"));
        privacySectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        return;
      }
      toast.error(t("missingFieldsWarning"));
      return;
    }

    if (!executeRecaptcha) {
      setErrorMessage(t("recaptchaError"));
      setIsError(true);
      return;
    }

    setSubmitting(true);

    try {
      const recaptchaToken = await executeRecaptcha("submitPreventiveRequest");

      const allChildrenAges = accommodations.flatMap((acc) => acc.childrenAges);

      const formattedRooms = accommodations.map((acc) => {
        const matchingRoom = rooms.find((r) => r.code === acc.preferenzaCamera);
        return {
          roomPreference:
            acc.preferenzaCamera && acc.preferenzaCamera !== "nessuna"
              ? matchingRoom?.description || acc.preferenzaCamera
              : null,
          roomTypeId: matchingRoom?.id ?? null,
          adults: acc.numAdulti,
          childrenAges: acc.childrenAges,
        };
      });

      const firstRoomSelected = rooms.find(
        (r) => r.code === accommodations[0]?.preferenzaCamera,
      );

      const fullPhone = telefono.trim()
        ? prefissoTelefono && !telefono.trim().startsWith("+")
          ? `${prefissoTelefono} ${telefono.trim()}`
          : telefono.trim()
        : "";

      const payload = {
        checkIn: checkin,
        checkOut: checkout,
        adultsNumber: totalAdults,
        childrenNumber: totalChildren,
        ageChildren: allChildrenAges,
        roomPreference:
          accommodations[0]?.preferenzaCamera &&
          accommodations[0]?.preferenzaCamera !== "nessuna"
            ? firstRoomSelected?.description ||
              accommodations[0]?.preferenzaCamera
            : null,
        roomTypeId: firstRoomSelected?.id ?? null,
        treatment:
          accommodations[0]?.trattamento &&
          accommodations[0]?.trattamento !== "nessuna"
            ? accommodations[0]?.trattamento
            : null,
        rooms: formattedRooms,
        // Legacy compatibility
        accommodations: accommodations.map((acc) => ({
          adultsNumber: acc.numAdulti,
          childrenNumber: acc.numBambini,
          ageChildren: acc.childrenAges,
          roomPreference: acc.preferenzaCamera,
          treatment: acc.trattamento,
        })),
        pets: animaliDomestici,
        name: nome.trim(),
        surname: cognome.trim(),
        language: lingua,
        nation: nazione?.iso2,
        city: citta.trim(),
        email: email.trim(),
        prefissoTelefono: prefissoTelefono,
        phone: fullPhone,
        notes: note.trim(),
        marketing: accettaMarketing,
        recaptchaToken,
      };

      axios.defaults.baseURL = environment.ELITE_BASE_URI;
      await axios.post(`/v1/preventiveRequest/${routeParams.id}`, payload);

      trackFormSubmitted(trackingConfig, {
        checkin,
        checkout,
        totalGuests,
        accommodationsCount: accommodations.length,
      });

      navigate("/confirm-preventive");
    } catch (err: any) {
      setIsError(true);
      setErrorMessage(
        err.response?.data?.message || err.message || t("connectionError"),
      );
      setLoading(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background">
        <LoadingItem size="large" />
        <p className="text-sm text-muted-foreground animate-pulse">
          {t("loading")}
        </p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="max-w-md w-full text-center shadow-lg border-destructive/20">
          <CardContent className="py-10 space-y-4">
            <div className="h-16 w-16 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-9 w-9 text-destructive" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight">{t("error")}</h2>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {errorMessage}
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => window.location.reload()}
            >
              {t("backToHome")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 pb-16">
      {/* Floating Toolbar with Dark Mode Toggle & Language Selector */}
      <div className="fixed top-3 right-3 md:top-5 md:right-5 z-50 bg-background/80 backdrop-blur-md border border-border/60 shadow-md rounded-full px-2 py-1 flex items-center gap-1">
        <ModeToggle />
        <Separator orientation="vertical" className="h-5 my-auto" />
        <LanguageSelector />
      </div>

      <div className="max-w-4xl mx-auto px-4 pt-6 md:pt-10 space-y-8">
        {/* Structure Hero Header */}
        <header className="text-center space-y-4">
          {structure?.logo ? (
            <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-card border border-border/70 shadow-sm mx-auto">
              <img
                src={structure.logo}
                alt={structure.name}
                className="h-14 md:h-16 max-w-[220px] object-contain"
              />
            </div>
          ) : null}

          <div className="space-y-2">
            <div className="flex items-center justify-center gap-1.5 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-foreground">
                {structure?.name || t("title")}
              </h1>
              {structure?.stars && structure.stars > 0 && (
                <div className="flex items-center gap-0.5 text-amber-500 fill-amber-500 ml-1">
                  {Array.from({ length: Math.min(5, structure.stars) }).map(
                    (_, i) => (
                      <Star
                        key={i}
                        className="h-4 w-4 fill-amber-400 text-amber-400"
                      />
                    ),
                  )}
                </div>
              )}
            </div>

            {(structure?.address || structure?.city) && (
              <p className="text-xs md:text-sm text-muted-foreground flex items-center justify-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-primary shrink-0" />
                <span>
                  {[structure.address, structure.city]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </p>
            )}

            <p className="text-sm md:text-base text-muted-foreground max-w-xl mx-auto leading-relaxed pt-1">
              {t("quoteSubtitle")}
            </p>
          </div>

          {/* Trust Value Badges */}
          <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
            <Badge
              variant="secondary"
              className="px-3 py-1 font-medium text-xs flex items-center gap-1.5 bg-background border"
            >
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              {t("freeQuote")}
            </Badge>
            <Badge
              variant="secondary"
              className="px-3 py-1 font-medium text-xs flex items-center gap-1.5 bg-background border"
            >
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
              {t("noCreditCard")}
            </Badge>
            <Badge
              variant="secondary"
              className="px-3 py-1 font-medium text-xs flex items-center gap-1.5 bg-background border"
            >
              <HeartHandshake className="h-3.5 w-3.5 text-primary" />
              {t("bestRate")}
            </Badge>
          </div>
        </header>

        {/* SECTION 1: DATES */}
        <Card
          ref={datesSectionRef}
          className="shadow-sm border-border/80 overflow-hidden"
        >
          <CardHeader className="pb-4 border-b bg-card/60">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
                1
              </div>
              <div>
                <CardTitle className="text-lg md:text-xl font-bold flex items-center gap-2">
                  <CalendarDays className="h-5 w-5 text-primary" />
                  {t("datesSection")}
                </CardTitle>
                <CardDescription className="text-xs md:text-sm">
                  {t("datesSubtitle")}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold flex items-center gap-1.5">
                  {t("checkin")} <span className="text-destructive">*</span>
                </Label>
                <DatePicker
                  value={checkin}
                  onChange={(date) => {
                    setCheckin(date);
                    if (checkout && date && date >= checkout) {
                      setCheckout(undefined);
                    }
                  }}
                  placeholder={t("selectDate")}
                  minDate={new Date()}
                  isDateDisabled={isDayDisabled}
                  className={`w-full h-11 ${
                    hasAttemptedSubmit && !checkin
                      ? "border-destructive ring-1 ring-destructive/30"
                      : ""
                  }`}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold flex items-center gap-1.5">
                  {t("checkout")} <span className="text-destructive">*</span>
                </Label>
                <DatePicker
                  value={checkout}
                  onChange={setCheckout}
                  placeholder={t("selectDate")}
                  minDate={checkoutMinDate}
                  disabled={!checkin}
                  className={`w-full h-11 ${
                    hasAttemptedSubmit && !checkout
                      ? "border-destructive ring-1 ring-destructive/30"
                      : ""
                  }`}
                />
              </div>
            </div>

            {/* Selected dates feedback summary */}
            {checkin && checkout && nightsCount > 0 ? (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-primary/5 border border-primary/20 rounded-xl p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5"
              >
                <div className="flex items-center gap-2.5 text-sm">
                  <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <CalendarCheck className="h-4 w-4" />
                  </div>
                  <div>
                    <span className="font-semibold text-foreground">
                      {nightsCount}{" "}
                      {nightsCount === 1 ? t("night") : t("nights")}
                    </span>
                    <span className="text-muted-foreground ml-1.5">
                      ({t("from")}{" "}
                      <strong className="text-foreground">
                        {formatDate(checkin, {
                          weekday: "short",
                          day: "numeric",
                          month: "short",
                        })}
                      </strong>{" "}
                      {t("to")}{" "}
                      <strong className="text-foreground">
                        {formatDate(checkout, {
                          weekday: "short",
                          day: "numeric",
                          month: "short",
                        })}
                      </strong>
                      )
                    </span>
                  </div>
                </div>

                {formConfig.minStay > 0 && (
                  <Badge
                    variant="outline"
                    className="text-xs shrink-0 self-start sm:self-auto bg-background"
                  >
                    {t("minStayNotice", { count: formConfig.minStay })}
                  </Badge>
                )}
              </motion.div>
            ) : formConfig.minStay > 0 ? (
              <p className="text-xs text-muted-foreground flex items-center gap-1 pt-1">
                <Info className="h-3.5 w-3.5 text-primary" />
                {t("minStayNotice", { count: formConfig.minStay })}
              </p>
            ) : null}

            {hasAttemptedSubmit && (!checkin || !checkout) && (
              <p className="text-xs text-destructive font-medium flex items-center gap-1.5 mt-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {t("datesRequired")}
              </p>
            )}
          </CardContent>
        </Card>

        {/* SECTION 2: ROOMS & GUESTS */}
        <Card
          ref={accommodationsSectionRef}
          className="shadow-sm border-border/80 overflow-hidden"
        >
          <CardHeader className="pb-4 border-b bg-card/60">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
                  2
                </div>
                <div>
                  <CardTitle className="text-lg md:text-xl font-bold flex items-center gap-2">
                    <Bed className="h-5 w-5 text-primary" />
                    {t("roomsAndGuestsSection")}
                  </CardTitle>
                  <CardDescription className="text-xs md:text-sm">
                    {t("roomsAndGuestsSubtitle")}
                  </CardDescription>
                </div>
              </div>

              <Badge
                variant="secondary"
                className="self-start sm:self-auto px-3 py-1 font-medium bg-background border"
              >
                <Users className="h-3.5 w-3.5 mr-1.5 text-primary" />
                {t("totalGuestsSummary", {
                  guests: totalGuests,
                  rooms: accommodations.length,
                })}
              </Badge>
            </div>
          </CardHeader>

          <CardContent className="p-6 space-y-6">
            <div className="space-y-4">
              <AnimatePresence initial={false}>
                {accommodations.map((acc, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Card className="border border-border/70 bg-card shadow-xs">
                      <CardHeader className="py-3 px-4 md:px-6 bg-muted/40 border-b flex flex-row items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm md:text-base text-foreground">
                            {t("accommodation")} {index + 1}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            ({acc.numAdulti} {t("adults").toLowerCase()}
                            {acc.numBambini > 0
                              ? `, ${acc.numBambini} ${t("children").toLowerCase()}`
                              : ""}
                            )
                          </span>
                        </div>
                        {accommodations.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 text-destructive hover:bg-destructive/10 hover:text-destructive gap-1 px-2.5 text-xs font-medium"
                            onClick={() =>
                              setAccommodations((prev) =>
                                prev.filter((_, i) => i !== index),
                              )
                            }
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">
                              {t("removeRoom")}
                            </span>
                          </Button>
                        )}
                      </CardHeader>

                      <CardContent className="p-4 md:p-6 space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {/* Adults counter */}
                          <div className="p-3.5 rounded-xl border bg-muted/20 flex items-center justify-between">
                            <div>
                              <Label className="text-sm font-semibold">
                                {t("adults")}
                              </Label>
                              <p className="text-xs text-muted-foreground">
                                {t("adultsDesc")}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-9 w-9 rounded-lg"
                                onClick={() =>
                                  updateAccommodation(
                                    index,
                                    "numAdulti",
                                    Math.max(1, acc.numAdulti - 1),
                                  )
                                }
                                disabled={acc.numAdulti <= 1}
                              >
                                -
                              </Button>
                              <span className="w-8 text-center font-bold text-base">
                                {acc.numAdulti}
                              </span>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-9 w-9 rounded-lg"
                                onClick={() =>
                                  updateAccommodation(
                                    index,
                                    "numAdulti",
                                    acc.numAdulti + 1,
                                  )
                                }
                              >
                                +
                              </Button>
                            </div>
                          </div>

                          {/* Children counter */}
                          {formConfig.showNumberChildren && (
                            <div className="p-3.5 rounded-xl border bg-muted/20 flex items-center justify-between">
                              <div>
                                <Label className="text-sm font-semibold">
                                  {t("children")}
                                </Label>
                                <p className="text-xs text-muted-foreground">
                                  {t("childrenDesc")}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-9 w-9 rounded-lg"
                                  onClick={() => {
                                    const newVal = Math.max(
                                      0,
                                      acc.numBambini - 1,
                                    );
                                    updateAccommodation(
                                      index,
                                      "numBambini",
                                      newVal,
                                    );
                                    updateAccommodation(
                                      index,
                                      "childrenAges",
                                      acc.childrenAges.slice(0, newVal),
                                    );
                                  }}
                                  disabled={acc.numBambini <= 0}
                                >
                                  -
                                </Button>
                                <span className="w-8 text-center font-bold text-base">
                                  {acc.numBambini}
                                </span>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-9 w-9 rounded-lg"
                                  onClick={() => {
                                    const max =
                                      formConfig.maxAgeChildren > 0
                                        ? formConfig.maxAgeChildren
                                        : 10;
                                    const newVal = acc.numBambini + 1;
                                    updateAccommodation(
                                      index,
                                      "numBambini",
                                      newVal,
                                    );
                                    updateAccommodation(index, "childrenAges", [
                                      ...acc.childrenAges,
                                      0,
                                    ]);
                                  }}
                                >
                                  +
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Children ages selector */}
                        {formConfig.showNumberChildren &&
                          acc.numBambini > 0 && (
                            <div className="p-4 rounded-xl bg-accent/30 border border-accent space-y-3">
                              <div className="flex items-center gap-2">
                                <HelpCircle className="h-4 w-4 text-primary" />
                                <Label className="text-sm font-semibold text-foreground">
                                  {t("childrenAgesTitle")}
                                </Label>
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                {Array.from({ length: acc.numBambini }).map(
                                  (_, childIndex) => {
                                    const ageVal =
                                      acc.childrenAges[childIndex] ?? 0;
                                    return (
                                      <div
                                        key={childIndex}
                                        className="space-y-1.5"
                                      >
                                        <Label className="text-xs text-muted-foreground font-medium">
                                          {t("childAge")} {childIndex + 1}
                                        </Label>
                                        <Select
                                          value={String(ageVal)}
                                          onValueChange={(val) => {
                                            const newAges = [
                                              ...acc.childrenAges,
                                            ];
                                            newAges[childIndex] = Number(val);
                                            updateAccommodation(
                                              index,
                                              "childrenAges",
                                              newAges,
                                            );
                                          }}
                                        >
                                          <SelectTrigger className="w-full bg-background h-10">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {Array.from({
                                              length:
                                                (formConfig.maxAgeChildren ||
                                                  17) + 1,
                                            }).map((_, age) => (
                                              <SelectItem
                                                key={age}
                                                value={String(age)}
                                              >
                                                {age === 0
                                                  ? "< 1 anno (0)"
                                                  : `${age} ${age === 1 ? "anno" : "anni"}`}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    );
                                  },
                                )}
                              </div>
                            </div>
                          )}

                        {/* Room and Board preferences */}
                        {(formConfig.showRooms || formConfig.showBoard) && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                            {formConfig.showRooms && (
                              <div className="space-y-2">
                                <Label className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                                  <Bed className="h-3.5 w-3.5 text-primary" />
                                  {t("roomPreferenceLabel")}
                                </Label>
                                <Select
                                  value={acc.preferenzaCamera}
                                  onValueChange={(val) =>
                                    updateAccommodation(
                                      index,
                                      "preferenzaCamera",
                                      val,
                                    )
                                  }
                                >
                                  <SelectTrigger className="w-full h-10">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="nessuna">
                                      {t("noPreference")}
                                    </SelectItem>
                                    {rooms.map((room) => (
                                      <SelectItem
                                        key={room.code}
                                        value={room.code}
                                      >
                                        {room.description}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}

                            {formConfig.showBoard && (
                              <div className="space-y-2">
                                <Label className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                                  <Utensils className="h-3.5 w-3.5 text-primary" />
                                  {t("treatmentLabel")}
                                </Label>
                                <Select
                                  value={acc.trattamento}
                                  onValueChange={(val) =>
                                    updateAccommodation(
                                      index,
                                      "trattamento",
                                      val,
                                    )
                                  }
                                >
                                  <SelectTrigger className="w-full h-10">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="nessuna">
                                      {t("noPreference")}
                                    </SelectItem>
                                    {boards.map((board) => (
                                      <SelectItem
                                        key={board.code}
                                        value={board.code}
                                      >
                                        {board.description}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Add Room button */}
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto h-11 border-dashed border-2 hover:border-primary hover:bg-primary/5 text-sm font-semibold gap-2"
              onClick={() =>
                setAccommodations([
                  ...accommodations,
                  {
                    numAdulti: 2,
                    numBambini: 0,
                    childrenAges: [],
                    preferenzaCamera: "nessuna",
                    trattamento: "nessuna",
                  },
                ])
              }
            >
              <Plus className="h-4 w-4 text-primary" />
              {t("addRoom")}
            </Button>

            {/* Pets toggle */}
            {formConfig.showPets && (
              <div className="pt-2">
                <div
                  className="p-4 rounded-xl border bg-muted/15 flex items-start gap-3 cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => setAnimaliDomestici((prev) => !prev)}
                >
                  <Checkbox
                    id="animali"
                    checked={animaliDomestici}
                    onCheckedChange={(checked) =>
                      setAnimaliDomestici(checked === true)
                    }
                    className="mt-0.5"
                  />
                  <div className="space-y-0.5">
                    <Label
                      htmlFor="animali"
                      className="cursor-pointer font-semibold text-sm flex items-center gap-1.5"
                    >
                      <PawPrint className="h-4 w-4 text-primary" />
                      {t("petsQuestion")}
                    </Label>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {t("petsDesc")}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* SECTION 3: CONTACT INFORMATION */}
        <Card
          ref={contactsSectionRef}
          className="shadow-sm border-border/80 overflow-hidden"
        >
          <CardHeader className="pb-4 border-b bg-card/60">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
                3
              </div>
              <div>
                <CardTitle className="text-lg md:text-xl font-bold flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  {t("contactData")}
                </CardTitle>
                <CardDescription className="text-xs md:text-sm">
                  {t("contactSubtitle")}
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label
                  htmlFor="nome"
                  className="text-sm font-semibold flex items-center gap-1"
                >
                  {t("firstName")} <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="nome"
                    autoComplete="given-name"
                    value={nome}
                    placeholder="Mario"
                    className={`h-11 ${
                      hasAttemptedSubmit && !nome.trim()
                        ? "border-destructive ring-1 ring-destructive/30"
                        : ""
                    }`}
                    onChange={(e) => setNome(e.target.value)}
                  />
                </div>
                {hasAttemptedSubmit && !nome.trim() && (
                  <p className="text-xs text-destructive font-medium flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {t("nameRequired")}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="cognome"
                  className="text-sm font-semibold flex items-center gap-1"
                >
                  {t("lastName")} <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="cognome"
                    autoComplete="family-name"
                    value={cognome}
                    placeholder="Rossi"
                    className={`h-11 ${
                      hasAttemptedSubmit && !cognome.trim()
                        ? "border-destructive ring-1 ring-destructive/30"
                        : ""
                    }`}
                    onChange={(e) => setCognome(e.target.value)}
                  />
                </div>
                {hasAttemptedSubmit && !cognome.trim() && (
                  <p className="text-xs text-destructive font-medium flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {t("surnameRequired")}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Email */}
              <div className="space-y-2">
                <Label
                  htmlFor="email"
                  className="text-sm font-semibold flex items-center gap-1"
                >
                  <Mail className="h-3.5 w-3.5 text-primary" />
                  {t("email")} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="mario.rossi@example.com"
                  value={email}
                  className={`h-11 ${
                    emailError
                      ? "border-destructive ring-1 ring-destructive/30"
                      : ""
                  }`}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  onBlur={() => {
                    setEmailTouched(true);
                  }}
                />
                {emailError && (
                  <p className="text-xs text-destructive font-medium flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {emailError}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label
                  htmlFor="telefono"
                  className="text-sm font-semibold flex items-center gap-1"
                >
                  <Phone className="h-3.5 w-3.5 text-primary" />
                  {t("phone")}
                </Label>
                <div className="flex gap-2">
                  <Select
                    value={prefissoTelefono}
                    onValueChange={setPrefissoTelefono}
                  >
                    <SelectTrigger className="w-[115px] shrink-0 h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {PHONE_PREFIXES.map((p) => (
                        <SelectItem key={p.code + p.name} value={p.code}>
                          <span className="flex items-center gap-2">
                            <span
                              className={`fi fi-${p.flag} rounded-xs text-xs`}
                            ></span>
                            <span>{p.code}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    id="telefono"
                    type="tel"
                    autoComplete="tel-national"
                    className="flex-1 h-11"
                    value={telefono}
                    placeholder="333 1234567"
                    onChange={(e) => setTelefono(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* City */}
              <div className="space-y-2">
                <Label
                  htmlFor="citta"
                  className="text-xs font-semibold text-muted-foreground flex items-center gap-1"
                >
                  <MapPin className="h-3.5 w-3.5 text-primary" />
                  {t("city")}
                </Label>
                <CitySelect
                  id="citta"
                  value={citta}
                  countryId={nazione?.id}
                  onChange={setCitta}
                  onSelectCity={(cityData) => {
                    setCitta(cityData.name);
                    if (cityData.country) {
                      setNazione({
                        id: cityData.country.id,
                        name: cityData.country.name,
                        iso2: cityData.country.iso2 || "",
                      });
                    } else if (cityData.countryIso2) {
                      setNazione((prev) =>
                        prev
                          ? { ...prev, iso2: cityData.countryIso2! }
                          : { id: 0, name: "", iso2: cityData.countryIso2! },
                      );
                    }
                  }}
                  placeholder={t("searchCityPlaceholder")}
                  className="h-11"
                />
              </div>

              {/* Nationality */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-muted-foreground">
                  {t("nationality")}
                </Label>
                <CountrySelect
                  value={nazione}
                  onValueChange={setNazione}
                  placeholder={t("selectNationality")}
                  apiEndpoint={`${environment.ELITE_BASE_URI}/geo/countries`}
                  className="h-11"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Language */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                  {t("language")} <span className="text-red-500">*</span>
                </Label>
                <Select value={lingua} onValueChange={setLingua}>
                  <SelectTrigger className="w-full h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="it">
                      <span className="flex items-center gap-2">
                        <span className="fi fi-it rounded-xs text-xs"></span>
                        Italiano
                      </span>
                    </SelectItem>
                    <SelectItem value="en">
                      <span className="flex items-center gap-2">
                        <span className="fi fi-gb rounded-xs text-xs"></span>
                        English
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* SECTION 4: NOTES */}
            {formConfig.showNote && (
              <div className="space-y-2 pt-2">
                <Label
                  htmlFor="note"
                  className="text-sm font-semibold flex items-center gap-1.5"
                >
                  <StickyNote className="h-4 w-4 text-primary" />
                  {t("notes")}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t("notesSubtitle")}
                </p>
                <Textarea
                  id="note"
                  rows={3}
                  placeholder={t("notesPlaceholder")}
                  value={note}
                  className="resize-none"
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* SECTION 5: PRIVACY & SUBMIT */}
        <Card
          ref={privacySectionRef}
          className="shadow-sm border-border/80 overflow-hidden"
        >
          <CardHeader className="pb-4 border-b bg-card/60">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
                4
              </div>
              <div>
                <CardTitle className="text-lg md:text-xl font-bold flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-primary" />
                  {t("privacySection")}
                </CardTitle>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-6 space-y-6">
            <div className="space-y-4">
              {/* Privacy Checkbox */}
              <div
                className={`p-3.5 rounded-xl border flex items-start gap-3 transition-colors ${
                  hasAttemptedSubmit && !accettaPrivacy
                    ? "border-destructive bg-destructive/5"
                    : "border-border bg-muted/10 hover:bg-muted/20"
                }`}
              >
                <Checkbox
                  id="privacy"
                  checked={accettaPrivacy}
                  onCheckedChange={(checked) =>
                    setAccettaPrivacy(checked === true)
                  }
                  className="mt-0.5"
                />
                <div className="space-y-1 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Label
                      htmlFor="privacy"
                      className="cursor-pointer text-sm font-medium leading-snug"
                    >
                      {t("privacyAccept")}{" "}
                      <span className="text-destructive">*</span>
                    </Label>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setPolicyModalType("privacy");
                        setIsPrivacyDialogOpen(true);
                      }}
                      className="inline-flex items-center justify-center size-5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors cursor-pointer"
                      title={t("privacySection")}
                      aria-label={t("privacySection")}
                    >
                      <Info className="size-3.5 text-primary" />
                    </button>
                  </div>
                  {hasAttemptedSubmit && !accettaPrivacy && (
                    <p className="text-xs text-destructive font-medium flex items-center gap-1">
                      <AlertCircle className="h-3 w-3 shrink-0" />
                      {t("privacyRequired")}
                    </p>
                  )}
                </div>
              </div>

              {/* Marketing Checkbox */}
              <div className="p-3.5 rounded-xl border border-border bg-muted/10 hover:bg-muted/20 flex items-start gap-3 transition-colors">
                <Checkbox
                  id="marketing"
                  checked={accettaMarketing}
                  onCheckedChange={(checked) =>
                    setAccettaMarketing(checked === true)
                  }
                  className="mt-0.5"
                />
                <div className="flex items-center gap-1.5 flex-wrap flex-1">
                  <Label
                    htmlFor="marketing"
                    className="cursor-pointer text-sm font-normal text-muted-foreground leading-snug"
                  >
                    {t("marketingAccept")}
                  </Label>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setPolicyModalType("marketing");
                      setIsPrivacyDialogOpen(true);
                    }}
                    className="inline-flex items-center justify-center size-5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors cursor-pointer"
                    title={t("marketingAccept")}
                    aria-label={t("marketingAccept")}
                  >
                    <Info className="size-3.5 text-primary" />
                  </button>
                </div>
              </div>
            </div>

            <PrivacyPolicyDialog
              open={isPrivacyDialogOpen}
              onOpenChange={setIsPrivacyDialogOpen}
              title={
                policyModalType === "marketing"
                  ? t("marketingAccept")
                  : t("privacySection")
              }
              htmlContent={
                policyModalType === "marketing"
                  ? marketingPolicyHtml
                  : privacyPolicyHtml
              }
              fallbackText={t("privacyAccept")}
            />

            {/* Trust highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 rounded-xl bg-muted/30 border text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-primary shrink-0" />
                <span>{t("trustGuarantee1")}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                <span>{t("trustGuarantee2")}</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary shrink-0" />
                <span>{t("trustGuarantee3")}</span>
              </div>
            </div>

            {/* Submit Button */}
            <div className="space-y-3 pt-2">
              <Button
                type="button"
                size="lg"
                className="w-full h-12 text-base font-semibold shadow-md btn-action gap-2 transition-all"
                disabled={submitting}
                onClick={handleSubmit}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>{t("submittingButton")}</span>
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    <span>{t("submit")}</span>
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
